import {customElement} from 'lit/decorators.js';
import {
  BehaviorSubject,
  combineLatest,
  combineLatestAll,
  Observable,
  of,
} from 'rxjs';

import {Activity} from './activity.js';
import Decimal from './break_infinity.js';
import {DynamicCost, DynamicValue} from './interfaces/dynamic-value';
import {Usable} from './interfaces/queueable.js';
import {ri} from './runner-idle.js';
import {UnlockableLitElement} from './unlockable-lit-element';

/**
 * A player-selectable option for an Activity.
 *
 * e.g. Different books for Read, types of bait for Fishing, etc.
 */
@customElement('ri-activity-option')
export class ActivityOption
  extends UnlockableLitElement
  implements Omit<Usable, 'perform'>
{
  // #region Properties (22)

  // protected override baseConditionString = 'Option';

  /** The activity this option is part of. */
  activity?: Activity;
  public override baseTags = new Set(['activity-option']);
  public chance!: DynamicValue;
  /** How likely is this to succeed? Range of 0 to 1 */
  public chance$: Observable<DynamicValue> = new BehaviorSubject(
    () => new Decimal(1)
  );
  /** Help text for the chance value. */
  chanceHelpText?: DynamicValue = () => '';
  /** User-friendly replacement for 'Chance of success'. */
  public chanceLabel: DynamicValue = () => 'Chance of success';
  public cost: DynamicCost | undefined;
  public effect: DynamicCost | undefined;
  // replacing below with unlock$
  // public override condition = () => {
  //   try {
  //     const unlock =
  //       (this.activity?.unlocked ?? false) &&
  //       (ri.get(this.name as Gettable)?.unlocked || // HACK?
  //         ([...this.unlockAtTags].every((tag) =>
  //           ri.currentLandmark?.tags.has(tag)
  //         ) &&
  //           typedEntries(this.requiredLandmarks).reduce(
  //             (acc, [k, v]) => acc && ri.landmarks[k].unlocked === v,
  //             true
  //           ) &&
  //           typedEntries(this.requiredStats).reduce(
  //             (acc, [k, v]) => acc && ri.stats[k].value.gte(v),
  //             true
  //           ) &&
  //           typedEntries(this.requiredResources).reduce(
  //             (acc, [k, v]) => acc && ri.resources[k].value.gte(v),
  //             true
  //           )));
  //     return unlock;
  //   } catch (e) {
  //     console.error(e);
  //     return true;
  //   }
  // };
  /** The cost of this option, if any. */
  public cost$: Observable<DynamicCost> = new BehaviorSubject(new Map());
  public description = '';
  public override displayName = 'Option';
  /**
   * The number of seconds the Activity will take to perform this option.
   * Undefined passes through to Activity.
   */
  public duration: number | undefined = undefined;
  /** The effect of this option, if any. */
  public effect$: Observable<DynamicCost> = new BehaviorSubject(new Map());
  /** The cost of this option if failed, if different from the normal cost. */
  failCost?: DynamicCost;
  public failCost$: Observable<DynamicCost> = new BehaviorSubject(new Map());
  /** The effect of this option if failed, if different from the normal effect. */
  failEffect?: DynamicCost;
  public failEffect$: Observable<DynamicCost> = new BehaviorSubject(new Map());
  public override hidden = false;
  public override landmarkTags = new Set<string>();
  public override name = 'option';
  public successfulUses$: BehaviorSubject<Decimal> = new BehaviorSubject(
    new Decimal(0)
  );
  public uses$: BehaviorSubject<Decimal> = new BehaviorSubject(new Decimal(0));

  // #endregion Properties (22)

  // #region Constructors (1)

  constructor() {
    super();
    // this.unlock$ = of(true);
    // this.visible$ = of(false); //this.activity?.unlock$ ?? NEVER;
  }

  // #endregion Constructors (1)

  // #region Public Methods (5)

  _updateTooltip?(): void;
  public onPerform(_step: number, _optionIndex: number) {}

  public override setup(overrides?: Partial<ActivityOption>): void {
    super.setup({...overrides});
    this.subscribe('chance', this.chance$);
    this.subscribe('cost', this.cost$);
    this.subscribe('effect', this.effect$);
  }

  public override tryUnlock(cheat?: boolean, quiet?: boolean): boolean {
    const unlocked = super.tryUnlock(cheat, quiet);
    if (unlocked) {
      this.activity?.requestUpdate();
    }
    return unlocked;
  }

  // #endregion Public Methods (5)
}
